import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import CreateIcon from '@mui/icons-material/Create';
import { useLanguage } from '../context/LanguageContext';
import { useTeluguYear } from '../context/TeluguYearContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { categorizeVatu, getKrishnaGrandhamInfo } from '../utils/krishnaYajurvedaUtils';
import PhotoUpload from '../components/PhotoUpload';
import SignatureCanvas from '../components/SignatureCanvas';

const ExaminerAssignments = () => {
  const { lang } = useLanguage();
  const { teluguYearTelugu } = useTeluguYear();
  const { user } = useAuth();

  // Only Admin can access this page
  const isAuthorized = user && user.role === 'Admin';

  // State
  const [parikshaTypes, setParikshaTypes] = useState([]);
  const [parikshaadhikaris, setParikshaadhikaris] = useState([]);
  const [parikshaBatches, setParikshaBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [parikshaDates, setParikshaDates] = useState([]);

  // Dialog states for Parikshaadhikari
  const [openParikshaadhikariDialog, setOpenParikshaadhikariDialog] = useState(false);
  const [editingParikshaadhikari, setEditingParikshaadhikari] = useState(null);
  const [originalParikshaTypes, setOriginalParikshaTypes] = useState([]);
  const [parikshaadhikariForm, setParikshaadhikariForm] = useState({
    name: '',
    selectedParikshaTypes: [],
    phone: '',
    unavailableDates: [],
    trustMember: false,
    attendance: false,
  });
  const [parikshaadhikariPhoto, setParikshaadhikariPhoto] = useState(null);
  const [parikshaadhikariPhotoConfig, setParikshaadhikariPhotoConfig] = useState({ fit: 'cover', position: 'center' });
  const [parikshaadhikariSignature, setParikshaadhikariSignature] = useState(null);

  // Dialog states for Batches
  const [openBatchDialog, setOpenBatchDialog] = useState(false);
  const [batchForm, setBatchForm] = useState({
    parikshaTypes: [],
  });
  const [currentBatchSetup, setCurrentBatchSetup] = useState({
    parikshaType: '',
    numberOfBatches: '',
  });
  const [batchExaminers, setBatchExaminers] = useState({});
  const [batchSpecialGhanam, setBatchSpecialGhanam] = useState({});
  const [allSelectedExaminers, setAllSelectedExaminers] = useState(new Set());
  const [isEditingBatches, setIsEditingBatches] = useState(false);

  // Vatu Assignment States
  const [assignmentMode, setAssignmentMode] = useState('');
  const [vatus, setVatus] = useState([]);
  const [vatuCategories, setVatuCategories] = useState({});
  const [vatuAssignments, setVatuAssignments] = useState({});
  const [savedVatuAssignments, setSavedVatuAssignments] = useState({});
  const [openManualAssignDialog, setOpenManualAssignDialog] = useState(false);
  const [selectedBatchForAssign, setSelectedBatchForAssign] = useState(null);
  const [selectedVatusForAssign, setSelectedVatusForAssign] = useState([]);
  const [pendingVatus, setPendingVatus] = useState([]);
  const [isLoadingVatus, setIsLoadingVatus] = useState(false);
  const [isSavingAssignments, setIsSavingAssignments] = useState(false);
  const [isAssigningVatus, setIsAssigningVatus] = useState(false);
  const [editingBatchId, setEditingBatchId] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState('');
  const [pendingModeChange, setPendingModeChange] = useState(null);
  const [hasPendingFirstRowChanges, setHasPendingFirstRowChanges] = useState(false);
  
  // Accordion expansion state
  const [accordionExpanded, setAccordionExpanded] = useState({
    parikshaadhikari: false,
    batches: false,
    savedAssignments: false
  });
  const [scrollPosition, setScrollPosition] = useState(0);

  // Labels
  const labels = {
    telugu: {
      parikshaadhikarulu: 'పరీక్షాధికారులు',
      parikshaBatches: 'పరీక్ష బ్యాచ్‌లు',
      addParikshaadhikari: 'పరీక్షాధికారి జోడించండి',
      addBatches: 'బ్యాచ్‌లను జోడించండి',
      name: 'పేరు',
      parikshaType: 'పరీక్ష రకం',
      phone: 'ఫోన్ నంబర్',
      availableDates: 'అందుబాటులో ఉన్న తేదీలు',
      trustMember: 'సంస్థ సభ్యుడు',
      create: 'సృష్టించండి',
      update: 'నవీకరించండి',
      cancel: 'రద్దు చేయండి',
      delete: 'తొలగించండి',
      edit: 'సవరించండి',
      numberOfBatches: 'బ్యాచ్‌ల సంఖ్య',
      sequence: 'క్రమం',
      selectExaminers: 'పరీక్షాధికారులను ఎంచుకోండి',
      saveSuccess: 'విజయవంతంగా సేవ్ చేయబడింది',
      deleteConfirm: 'నిజమైన తొలగించడం?',
    },
    sanskrit: {
      parikshaadhikarulu: 'परीक्षाधिकारिणः',
      parikshaBatches: 'परीक्षा समूहाः',
      addParikshaadhikari: 'परीक्षाधिकारि जोडयतु',
      addBatches: 'समूहान् जोडयतु',
      name: 'नाम',
      parikshaType: 'परीक्षा प्रकारः',
      phone: 'फोन नंबर',
      availableDates: 'उपलब्ध तिथयः',
      trustMember: 'संस्था सदस्य',
      create: 'सृजयतु',
      update: 'संशोधयतु',
      cancel: 'रद्दु कुरु',
      delete: 'विलोपयतु',
      edit: 'सम्पादयतु',
      numberOfBatches: 'समूहानां संख्या',
      sequence: 'क्रमः',
      selectExaminers: 'परीक्षाधिकारिणः वरणयतु',
      saveSuccess: 'सफलतया रक्षितम्',
      deleteConfirm: 'वास्तवे विलोपयितुम्?',
    },
  };

  const currentLabels = labels[lang];

  // Fetch data when teluguYearTelugu is available
  useEffect(() => {
    if (teluguYearTelugu) {
      fetchData();
    }
  }, [teluguYearTelugu]);

  // Fetch vatus when savedVatuAssignments are loaded
  useEffect(() => {
    if (Object.keys(savedVatuAssignments).length > 0 && vatus.length === 0) {
      fetchVatus();
    }
  }, [savedVatuAssignments]);

  // Refresh batches data when pariksha types change
  const refreshParikshaBatches = async () => {
    try {
      if (!teluguYearTelugu) return;
      
      const batchRes = await api.get(`/api/pariksha-batch/year/${teluguYearTelugu}`);
      const batchData = await batchRes.json();
      if (batchRes.ok && batchData.success) {
        setParikshaBatches(batchData.data);
      }
    } catch (err) {
      console.error('Error refreshing pariksha batches:', err);
    }
  };

  const fetchData = async () => {
    try {
      if (!teluguYearTelugu) {
        console.warn('teluguYearTelugu not available yet');
        return;
      }

      // Ensure token is available before making requests
      const token = localStorage.getItem('accessToken');
      if (!token) {
        console.warn('No access token found');
        setError('Session expired. Please login again.');
        return;
      }

      setLoading(true);
      
      // Fetch pariksha types
      const typesRes = await api.get('/api/pariksha-types');
      const typesData = await typesRes.json();
      if (typesRes.ok && typesData.success) {
        setParikshaTypes(typesData.data);
      }

      // Fetch pariksha dates for unavailable dates picker
      const datesRes = await api.get(`/api/parikshaadhikari/pariksha-dates/${teluguYearTelugu}`);
      const datesData = await datesRes.json();
      if (datesRes.ok && datesData.success) {
        setParikshaDates(datesData.data);
      }

      // Fetch parikshaadhikaris
      const adhRes = await api.get(`/api/parikshaadhikari/year/${teluguYearTelugu}`);
      const adhData = await adhRes.json();
      if (adhRes.ok && adhData.success) {
        setParikshaadhikaris(adhData.data);
      }

      // Fetch batches
      const batchRes = await api.get(`/api/pariksha-batch/year/${teluguYearTelugu}`);
      const batchData = await batchRes.json();
      if (batchRes.ok && batchData.success) {
        setParikshaBatches(batchData.data);
      }

      // Fetch vatu assignments
      const assignRes = await api.get(`/api/vatu-assignments/${teluguYearTelugu}`);
      const assignData = await assignRes.json();
      if (assignRes.ok && assignData.success) {
        // Convert grouped assignments to savedVatuAssignments format with vatu details
        const assignments = {};
        Object.entries(assignData.data).forEach(([batchId, batchData]) => {
          assignments[batchId] = batchData.vatus.map(v => {
            return {
              vatu_id: v.vatu._id,
              vatu_name: v.vatu.vatu_name || v.vatu._id
            };
          });
        });
        setSavedVatuAssignments(assignments);
        
        // Set assignment mode from first assignment if exists
        const firstAssignment = Object.values(assignData.data)[0];
        if (firstAssignment && firstAssignment.vatus.length > 0) {
          const mode = firstAssignment.vatus[0].assignment_mode || 'manual';
          setAssignmentMode(mode);
        }
      }

      setError('');
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Convert name to Telugu/Sanskrit
  const convertNameWithSuffix = (name, inputLang) => {
    // Placeholder for language conversion - you may integrate with actual conversion library
    const telugu = name;
    const sanskrit = name;

    // Add suffix/prefix based on language
    const teluguWithSuffix = inputLang === 'telugu' ? `${telugu} గారు` : telugu;
    const sanskritWithSuffix = `श्री ${sanskrit}`;

    return {
      telugu: teluguWithSuffix,
      sanskrit: sanskritWithSuffix,
    };
  };

  // Handle Parikshaadhikari form
  const handleOpenParikshaadhikariDialog = (item = null) => {
    // Save current scroll position before opening dialog
    setScrollPosition(window.scrollY);
    
    if (item) {
      setEditingParikshaadhikari(item);
      // Save original pariksha types for comparison later
      setOriginalParikshaTypes(item.pariksha_types || []);
      // Convert available_dates back to unavailable_dates for editing
      const availableDateSet = new Set(
        (item.available_dates || []).map(d => new Date(d).toLocaleDateString('en-CA'))
      );
      const unavailableDates = parikshaDates.filter(d => {
        const dateStr = new Date(d).toLocaleDateString('en-CA');
        return !availableDateSet.has(dateStr);
      });
      
      // Check if today's date is in attendance_dates array
      const today = new Date().toLocaleDateString('en-CA'); // 'en-CA' locale gives YYYY-MM-DD
      const attendanceDatesArray = Array.isArray(item.attendance_dates) ? item.attendance_dates : [];
      const isTodayPresent = attendanceDatesArray.includes(today);
      
      setParikshaadhikariForm({
        name: item.telugu.replace(' గారు', '').replace('श्री ', ''),
        selectedParikshaTypes: item.pariksha_types || [],
        phone: item.phone,
        unavailableDates: unavailableDates,
        trustMember: item.trust_member,
        attendance: isTodayPresent,
      });
      
      // Load photo and signature
      setParikshaadhikariPhoto(item.photo_download_link || null);
      setParikshaadhikariPhotoConfig(item.photo_object_config || { fit: 'cover', position: 'center' });
      setParikshaadhikariSignature(item.signature_download_link || null);
    } else {
      setEditingParikshaadhikari(null);
      setOriginalParikshaTypes([]);
      setParikshaadhikariForm({
        name: '',
        selectedParikshaTypes: [],
        phone: '',
        unavailableDates: [],
        trustMember: false,
        attendance: false,
      });
      setParikshaadhikariPhoto(null);
      setParikshaadhikariPhotoConfig({ fit: 'cover', position: 'center' });
      setParikshaadhikariSignature(null);
    }
    setOpenParikshaadhikariDialog(true);
  };

  const handleCloseParikshaadhikariDialog = () => {
    setOpenParikshaadhikariDialog(false);
    setEditingParikshaadhikari(null);
    // Restore scroll position when closing dialog
    window.scrollTo(0, scrollPosition);
  };

  const handleSaveParikshaadhikari = async () => {
    try {
      const { telugu, sanskrit } = convertNameWithSuffix(parikshaadhikariForm.name, lang);

      // Handle attendance_dates: add today's date if checked, remove if unchecked
      const today = new Date().toLocaleDateString('en-CA'); // 'en-CA' locale gives YYYY-MM-DD
      const existingDates = editingParikshaadhikari?.attendance_dates || [];
      
      let updatedAttendanceDates;
      if (parikshaadhikariForm.attendance) {
        // If attendance is checked, add today's date (if not already present)
        updatedAttendanceDates = Array.from(new Set([...existingDates, today]));
      } else {
        // If attendance is unchecked, remove today's date
        updatedAttendanceDates = existingDates.filter(date => date !== today);
      }

      // Detect deselected pariksha types (if editing existing parikshaadhikari)
      const deselectedParikshaTypes = editingParikshaadhikari
        ? originalParikshaTypes.filter(type => !parikshaadhikariForm.selectedParikshaTypes.includes(type))
        : [];

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('telugu', telugu);
      formData.append('sanskrit', sanskrit);
      formData.append('pariksha_types', JSON.stringify(parikshaadhikariForm.selectedParikshaTypes));
      formData.append('phone', parikshaadhikariForm.phone);
      formData.append('unavailable_dates', JSON.stringify(parikshaadhikariForm.unavailableDates));
      formData.append('trust_member', parikshaadhikariForm.trustMember);
      formData.append('attendance_dates', JSON.stringify(updatedAttendanceDates));
      formData.append('photo_object_config', JSON.stringify(parikshaadhikariPhotoConfig));
      
      if (!editingParikshaadhikari) {
        formData.append('year', teluguYearTelugu);
      }
      
      // Append photo if it's a File object
      if (parikshaadhikariPhoto && parikshaadhikariPhoto instanceof File) {
        formData.append('photo', parikshaadhikariPhoto);
      }
      
      // Append signature if it's a File object
      if (parikshaadhikariSignature && parikshaadhikariSignature instanceof File) {
        formData.append('signature', parikshaadhikariSignature);
      }

      let res;
      if (editingParikshaadhikari) {
        res = await api.put(`/api/parikshaadhikari/${editingParikshaadhikari._id}`, formData);
      } else {
        res = await api.post('/api/parikshaadhikari', formData);
      }

      const data = await res.json();
      if (res.ok && data.success) {
        // If there are deselected pariksha types, remove parikshaadhikari from those batches
        if (deselectedParikshaTypes.length > 0 && editingParikshaadhikari) {
          try {
            await api.post('/api/pariksha-batch/remove-parikshaadhikari-from-type', {
              parikshaadhikari_id: editingParikshaadhikari._id,
              pariksha_types: deselectedParikshaTypes,
              year: editingParikshaadhikari.year,
            });
          } catch (err) {
            console.error('Error removing parikshaadhikari from batches:', err);
            // Continue despite this error - the parikshaadhikari was still updated
          }
        }

        // Update only the specific item in the list instead of fetching all data
        if (editingParikshaadhikari) {
          // Update existing item
          const updatedParikshaadhikaris = parikshaadhikaris.map((item) =>
            item._id === editingParikshaadhikari._id ? data.data : item
          );
          setParikshaadhikaris(updatedParikshaadhikaris);
        } else {
          // Add new item
          setParikshaadhikaris([...parikshaadhikaris, data.data]);
        }
        handleCloseParikshaadhikariDialog();
        
        // Refresh batch assignments after parikshaadhikari is saved (pariksha types may have changed)
        await refreshParikshaBatches();
      } else {
        setError(data.error || 'Failed to save');
      }
    } catch (err) {
      console.error('Error saving parikshaadhikari:', err);
      setError('Failed to save parikshaadhikari');
    }
  };

  // Handle reordering
  const handleMoveParikshaadhikari = async (index, direction) => {
    const newList = [...parikshaadhikaris];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= newList.length) return;

    [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];

    try {
      const ids = newList.map((item) => item._id);
      const res = await api.post('/api/parikshaadhikari/reorder', { reorderedIds: ids });
      const data = await res.json();
      if (res.ok && data.success) {
        setParikshaadhikaris(newList);
      }
    } catch (err) {
      console.error('Error reordering:', err);
    }
  };

  // Handle deactivate/reactivate
  const handleToggleActiveParikshaadhikari = async (id, currentlyActive) => {
    const action = currentlyActive ? 'deactivate' : 'reactivate';
    const confirmMessage = currentlyActive 
      ? (lang === 'telugu' ? 'నిజంగా నిష్క్రియం చేయాలా?' : 'वास्तवे निष्क्रियं करणीयम्?')
      : (lang === 'telugu' ? 'నిజంగా తిరిగి సక్రియం చేయాలా?' : 'वास्तवे पुनः सक्रियं करणीयम्?');
    
    if (!window.confirm(confirmMessage)) return;

    try {
      let res;
      if (currentlyActive) {
        // Deactivate
        res = await api.delete(`/api/parikshaadhikari/${id}`);
      } else {
        // Reactivate
        res = await api.post(`/api/parikshaadhikari/${id}/reactivate`, {});
      }
      const data = await res.json();
      if (res.ok && data.success) {
        fetchData();
      }
    } catch (err) {
      console.error(`Error ${action}:`, err);
      setError(`Failed to ${action}`);
    }
  };

  // Handle Batch Dialog
  const handleOpenBatchDialog = () => {
    setBatchForm({ parikshaTypes: [] });
    setCurrentBatchSetup({ parikshaType: '', numberOfBatches: '' });
    setBatchExaminers({});
    setBatchSpecialGhanam({});
    setAllSelectedExaminers(new Set());
    setIsEditingBatches(false);
    setOpenBatchDialog(true);
  };

  const handleEditBatches = () => {
    // Group existing batches by pariksha_type
    const batchesByType = {};
    const examinersByBatch = {};
    const specialGhanams = {};

    parikshaBatches.forEach((batch) => {
      const type = batch.pariksha_type;
      
      if (!batchesByType[type]) {
        batchesByType[type] = {
          parikshaType: type,
          numberOfBatches: 0,
        };
      }
      
      // Track the highest batch number for this type
      const batchNum = batch.batch_number || 1;
      if (batchNum > batchesByType[type].numberOfBatches) {
        batchesByType[type].numberOfBatches = batchNum;
      }
    });

    // Convert grouped batches to array format FIRST to get correct setIndex
    const parikshaTypesArray = Object.values(batchesByType);

    // Now map examiners with correct setIndex based on position in parikshaTypesArray
    parikshaBatches.forEach((batch) => {
      const type = batch.pariksha_type;
      const batchNum = batch.batch_number || 1;
      
      // Find the setIndex of this type in the parikshaTypesArray
      const setIndex = parikshaTypesArray.findIndex(item => item.parikshaType === type);
      const batchKey = `${type}_${setIndex}_${batchNum}`;
      
      // Extract _id from each examiner object (since batch.parikshaadhikari_ids is populated with full objects)
      examinersByBatch[batchKey] = (batch.parikshaadhikari_ids || []).map((examiner) => 
        typeof examiner === 'string' ? examiner : examiner._id
      );

      // Track special ghanam status
      if (batch.is_special_ghanam) {
        specialGhanams[batchKey] = true;
      }
    });

    // Populate dialog with existing data
    setBatchForm({ parikshaTypes: parikshaTypesArray });
    setBatchExaminers(examinersByBatch);
    setBatchSpecialGhanam(specialGhanams);
    
    // Set all selected examiners
    const allSelected = new Set();
    Object.values(examinersByBatch).forEach((examinerIds) => {
      examinerIds.forEach((id) => allSelected.add(id));
    });
    setAllSelectedExaminers(allSelected);

    setCurrentBatchSetup({ parikshaType: '', numberOfBatches: '' });
    setOpenBatchDialog(true);
    setIsEditingBatches(true);
  };

  const handleCloseBatchDialog = () => {
    setOpenBatchDialog(false);
  };

  const handleAddParikshaBatchSet = () => {
    if (!currentBatchSetup.parikshaType || !currentBatchSetup.numberOfBatches) return;

    const newBatchSet = {
      parikshaType: currentBatchSetup.parikshaType,
      numberOfBatches: parseInt(currentBatchSetup.numberOfBatches, 10),
    };

    setBatchForm({
      parikshaTypes: [...batchForm.parikshaTypes, newBatchSet],
    });

    setCurrentBatchSetup({ parikshaType: '', numberOfBatches: '' });
  };

  const handleRemoveParikshaBatchSet = (index) => {
    const newParikshaTypes = batchForm.parikshaTypes.filter((_, i) => i !== index);
    setBatchForm({ parikshaTypes: newParikshaTypes });

    const batchSetToRemove = batchForm.parikshaTypes[index];
    const parikshaTypeToRemove = batchSetToRemove.parikshaType;
    const setIndexToRemove = index;
    const newBatchExaminers = { ...batchExaminers };
    const newBatchSpecialGhanam = { ...batchSpecialGhanam };
    const newSelectedExaminers = new Set(allSelectedExaminers);

    Object.keys(newBatchExaminers).forEach((key) => {
      // Key format is: parikshaType_setIndex_batchNumber
      if (key.startsWith(`${parikshaTypeToRemove}_${setIndexToRemove}_`)) {
        (newBatchExaminers[key] || []).forEach((id) => newSelectedExaminers.delete(id));
        delete newBatchExaminers[key];
      }
    });
    
    Object.keys(newBatchSpecialGhanam).forEach((key) => {
      if (key.startsWith(`${parikshaTypeToRemove}_${setIndexToRemove}_`)) {
        delete newBatchSpecialGhanam[key];
      }
    });

    setBatchExaminers(newBatchExaminers);
    setBatchSpecialGhanam(newBatchSpecialGhanam);
    setAllSelectedExaminers(newSelectedExaminers);
  };

  const getFilteredExaminers = (parikshaType) => {
    return parikshaadhikaris.filter(
      (examiner) =>
        examiner.active !== false &&
        (!examiner.pariksha_types || examiner.pariksha_types.includes(parikshaType)) &&
        !allSelectedExaminers.has(examiner._id)
    );
  };

  const getExaminerById = (id) => {
    // Direct match by _id
    let examiner = parikshaadhikaris.find((e) => e._id === id);
    
    // If not found and id looks like a string, try string comparison
    if (!examiner && typeof id === 'string') {
      examiner = parikshaadhikaris.find((e) => String(e._id) === String(id));
    }
    
    // If still not found, try to find by telugu name as fallback
    if (!examiner && typeof id === 'string') {
      examiner = parikshaadhikaris.find(
        (e) => e.telugu?.includes(id.substring(0, 5)) || e.sanskrit?.includes(id.substring(0, 5))
      );
    }
    
    return examiner;
  };

  const getAssociationMembers = (parikshaType) => {
    return getFilteredExaminers(parikshaType).filter((examiner) => examiner.trust_member === true);
  };

  const getRegularMembers = (parikshaType) => {
    return getFilteredExaminers(parikshaType).filter((examiner) => examiner.trust_member !== true);
  };

  // Fetch vatus without photos
  const fetchVatus = async () => {
    try {
      setIsLoadingVatus(true);
      // Get today's date in YYYY-MM-DD format using local timezone (not UTC)
      const today = new Date().toLocaleDateString('en-CA'); // 'en-CA' locale gives YYYY-MM-DD
      const res = await api.get(`/api/vatu?attendance_date=${today}&completed=true`);
      const data = await res.json();
      if (res.ok && data.success) {
        setVatus(data.data || []);

        // Categorize vatus
        const categoriesMap = {};
        data.data.forEach(vatu => {
          categoriesMap[vatu.vatu_id] = categorizeVatu(data.data.find(v => v.vatu_id === vatu.vatu_id), lang);
        });
        console.log('Vatu Categories:', categoriesMap);
        setVatuCategories(categoriesMap);

        // Initialize pending vatus (all vatus not yet assigned)
        setPendingVatus(data.data.map(v => v.vatu_id));

        // Return the fetched data for immediate use
        return { vatus: data.data || [], categoriesMap };
      }
      return { vatus: [], categoriesMap: {} };
    } catch (err) {
      console.error('Error fetching vatus:', err);
      setError('Failed to fetch vatus');
      return { vatus: [], categoriesMap: {} };
    } finally {
      setIsLoadingVatus(false);
    }
  };

  // Auto-assign vatus to batches with uniform category distribution
  const autoAssignVatus = async (vatusList, vatuCategoriesMap) => {
    try {
      const assignments = {};
      
      console.log('DEBUG autoAssignVatus - vatusList count:', vatusList.length);
      console.log('DEBUG autoAssignVatus - parikshaBatches count:', parikshaBatches.length);
      
      // Initialize assignments for ALL batches
      parikshaBatches.forEach(batch => {
        assignments[batch._id] = [];
      });

      // Get batches grouped by pariksha_type
      const batchesByType = {};
      parikshaBatches.forEach(batch => {
        const type = batch.pariksha_type;
        if (!batchesByType[type]) {
          batchesByType[type] = {
            nonSpecial: [],
            special: []
          };
        }
        if (batch.is_special_ghanam) {
          batchesByType[type].special.push(batch);
        } else {
          batchesByType[type].nonSpecial.push(batch);
        }
      });

      console.log('DEBUG batchesByType:', batchesByType);

      // Track assigned vatus to avoid duplicates
      const assignedVatus = new Set();

      // GROUP VATUS BY CATEGORY FOR UNIFORM DISTRIBUTION
      const vatusByCategory = {};
      vatusList.forEach(vatu => {
        const category = vatuCategoriesMap[vatu.vatu_id] || 'Mulam - Junior';
        if (!vatusByCategory[category]) {
          vatusByCategory[category] = [];
        }
        vatusByCategory[category].push(vatu);
      });

      console.log('DEBUG vatusByCategory:', Object.keys(vatusByCategory).map(cat => `${cat}: ${vatusByCategory[cat].length}`));

      // PROCESS EACH CATEGORY AND DISTRIBUTE UNIFORMLY
      Object.entries(vatusByCategory).forEach(([category, categoryVatus]) => {
        console.log(`\n📊 Processing category: ${category} with ${categoryVatus.length} vatus`);
        
        categoryVatus.forEach((vatu, index) => {
          const parikshaTypes = Array.isArray(vatu.pariksha_type) ? vatu.pariksha_type : [vatu.pariksha_type];

          // Process each pariksha_type for this vatu
          parikshaTypes.forEach(parikshaType => {
            const batches = batchesByType[parikshaType];
            
            if (!batches || (!batches.nonSpecial.length && !batches.special.length)) {
              console.warn(`No batches found for pariksha_type: ${parikshaType} for vatu: ${vatu.vatu_id}`);
              return;
            }

            // Helper function to check if vatu already assigned
            const isVatuAssignedToBatch = (batchId, vatuId) => {
              return assignments[batchId].some(item => 
                typeof item === 'string' ? item === vatuId : item.vatu_id === vatuId
              );
            };

            // LAKSHANAM: Assign to Krishna_Yajurveda special batch
            if (parikshaType === 'lakshanam') {
              const krishnaSpecialBatches = batchesByType['krishna_yajurveda']?.special;
              if (krishnaSpecialBatches && krishnaSpecialBatches.length > 0) {
                const specialBatch = krishnaSpecialBatches[0];
                if (!isVatuAssignedToBatch(specialBatch._id, vatu.vatu_id)) {
                  assignments[specialBatch._id].push({
                    vatu_id: vatu.vatu_id,
                    vatu_name: vatu.vatu_name
                  });
                  assignedVatus.add(vatu.vatu_id);
                }
              }
            }
            
            // KRISHNA_YAJURVEDA: Special vatus to special batch, distribute others uniformly by category
            else if (parikshaType === 'krishna_yajurveda') {
              // Assign special vatus to special batch
              if (category === 'Special' && batches.special.length > 0) {
                const specialBatch = batches.special[0];
                if (!isVatuAssignedToBatch(specialBatch._id, vatu.vatu_id)) {
                  assignments[specialBatch._id].push({
                    vatu_id: vatu.vatu_id,
                    vatu_name: vatu.vatu_name
                  });
                  assignedVatus.add(vatu.vatu_id);
                }
              }
              // Distribute non-special vatus uniformly across non-special batches by category
              else if (batches.nonSpecial.length > 0) {
                // Use modulo to distribute uniformly within category
                const batchIndex = index % batches.nonSpecial.length;
                const targetBatch = batches.nonSpecial[batchIndex];
                
                if (!isVatuAssignedToBatch(targetBatch._id, vatu.vatu_id)) {
                  assignments[targetBatch._id].push({
                    vatu_id: vatu.vatu_id,
                    vatu_name: vatu.vatu_name
                  });
                  assignedVatus.add(vatu.vatu_id);
                  console.log(`  ✓ ${vatu.vatu_name} (${category}) → Batch ${batchIndex + 1}`);
                }
              }
            }
            
            // SANSKRIT and vedadhyayi_sahityam: Uniformly distribute across all batches by category
            else if (parikshaType === 'sanskrit' || parikshaType === 'vedadhyayi_sahityam') {
              const allBatches = [...(batches.special || []), ...(batches.nonSpecial || [])];
              if (allBatches.length > 0) {
                // Use modulo to distribute uniformly within category
                const batchIndex = index % allBatches.length;
                const targetBatch = allBatches[batchIndex];
                
                if (!isVatuAssignedToBatch(targetBatch._id, vatu.vatu_id)) {
                  assignments[targetBatch._id].push({
                    vatu_id: vatu.vatu_id,
                    vatu_name: vatu.vatu_name
                  });
                  assignedVatus.add(vatu.vatu_id);
                  console.log(`  ✓ ${vatu.vatu_name} (${category}) → Batch ${batchIndex + 1}`);
                }
              }
            }
          });
        });
      });

      console.log('DEBUG final assignments:', assignments);
      console.log('DEBUG assigned vatus count:', assignedVatus.size);

      setVatuAssignments(assignments);
      setPendingVatus([]);
      setAssignmentMode('auto'); // Set to 'auto' not empty string
      
      // Save auto-assigned vatus to database
      await handleSaveAssignmentsToDb(assignments);
    } catch (err) {
      console.error('Error in auto-assign:', err);
      setError('Failed to auto-assign vatus');
    }
  };

  // Handle manual vatu assignment
  const handleManualAssignment = async () => {
    if (!selectedBatchForAssign || selectedVatusForAssign.length === 0) {
      setError('Please select both batch and at least one vatu');
      return;
    }

    try {
      setIsAssigningVatus(true);
      const newAssignments = { ...vatuAssignments };
      const newPendingVatus = [...pendingVatus];
      
      // If editing, remove old vatus from pending and clear old assignment
      if (editingBatchId) {
        const oldVatus = vatuAssignments[editingBatchId] || [];
        oldVatus.forEach(vatuData => {
          const vatuId = typeof vatuData === 'string' ? vatuData : vatuData.vatu_id;
          if (!newPendingVatus.includes(vatuId) && !selectedVatusForAssign.includes(vatuId)) {
            newPendingVatus.push(vatuId);
          }
        });
      }
      
      if (!newAssignments[selectedBatchForAssign._id]) {
        newAssignments[selectedBatchForAssign._id] = [];
      }
      
      // Add selected vatus to the batch - transform to include vatu_name
      newAssignments[selectedBatchForAssign._id] = selectedVatusForAssign.map(vatuId => {
        const vatu = vatus.find(v => v.vatu_id === vatuId);
        return {
          vatu_id: vatuId,
          vatu_name: vatu?.vatu_name || vatuId
        };
      });
      
      // Remove assigned vatus from pending
      const finalPendingVatus = newPendingVatus.filter(id => !selectedVatusForAssign.includes(id));
      
      setVatuAssignments(newAssignments);
      setPendingVatus(finalPendingVatus);
      
      // Reset for next assignment
      setSelectedBatchForAssign(null);
      setSelectedVatusForAssign([]);
      setEditingBatchId(null);
      setHasPendingFirstRowChanges(false);
      
    } catch (err) {
      console.error('Error in manual assignment:', err);
      setError('Failed to assign vatus');
    } finally {
      setIsAssigningVatus(false);
    }
  };

  // Save assignments to database
  const handleSaveAssignmentsToDb = async (assignmentsToSave) => {
    try {
      setIsSavingAssignments(true);
      const res = await api.post('/api/vatu-assignments', {
        year: teluguYearTelugu,
        assignments: assignmentsToSave,
        assignment_mode: assignmentMode // Include the current assignment mode
      });
      
      const data = await res.json();
      if (res.ok && data.success) {
        setError('');
        // Update saved assignments with the new data
        setSavedVatuAssignments(assignmentsToSave);
        // Also update current dialog display
        setVatuAssignments(assignmentsToSave);
        
        // Clear first row but keep all assignment cards visible
        setSelectedBatchForAssign(null);
        setSelectedVatusForAssign([]);
        setEditingBatchId(null);
        setHasPendingFirstRowChanges(false);
        
        // Dialog will be closed by the caller
      } else {
        setError(data.error || 'Failed to save assignments');
        alert(lang === 'telugu' ? 'సేవ్ చేయడం విఫలమైంది: ' + (data.error || '') : 'सँरक्षण असफल: ' + (data.error || ''));
      }
    } catch (err) {
      console.error('Error saving assignments:', err);
      setError('Failed to save assignments: ' + err.message);
      alert('Error: ' + err.message);
    } finally {
      setIsSavingAssignments(false);
    }
  };

  const handleSaveBatches = async () => {
    try {
      if (isEditingBatches) {
        // EDITING MODE: Update existing batches, create new ones, AND delete removed ones
        const updates = [];
        const newBatches = [];
        const batchesInForm = new Set(); // Track which batches are still in the form

        batchForm.parikshaTypes.forEach((batchSet, setIndex) => {
          const numberOfBatches = batchSet.numberOfBatches;
          const parikshaType = parikshaTypes.find((pt) => pt.value === batchSet.parikshaType);

          for (let i = 1; i <= numberOfBatches; i++) {
            const batchKey = `${batchSet.parikshaType}_${setIndex}_${i}`;
            
            // Find the matching existing batch
            const existingBatch = parikshaBatches.find(
              (batch) =>
                batch.pariksha_type === batchSet.parikshaType && batch.batch_number === i
            );

            if (existingBatch) {
              // Track that this batch is still in the form
              batchesInForm.add(existingBatch._id);
              
              // Update existing batch if changed
              const newParikshaadhikariIds = batchExaminers[batchKey] || [];
              const newIsSpecialGhanam =
                batchSet.parikshaType === 'krishna_yajurveda' ? (batchSpecialGhanam[batchKey] || false) : false;
              
              // Extract IDs from existing batch parikshaadhikari_ids (could be objects or strings)
              const existingIds = (existingBatch.parikshaadhikari_ids || []).map((examiner) =>
                typeof examiner === 'string' ? examiner : examiner._id
              );
              
              // Check if there are any actual changes
              const idsChanged = JSON.stringify(newParikshaadhikariIds.sort()) !== JSON.stringify(existingIds.sort());
              const ghanumChanged = newIsSpecialGhanam !== existingBatch.is_special_ghanam;
              
              // Only add to updates if something changed
              if (idsChanged || ghanumChanged) {
                updates.push({
                  batchId: existingBatch._id,
                  parikshaadhikari_ids: newParikshaadhikariIds,
                  is_special_ghanam: newIsSpecialGhanam,
                });
              }
            } else {
              // Create new batch
              const batchName = `${lang === 'telugu' ? parikshaType.label.telugu : parikshaType.label.sanskrit} - బ్యాచ్ ${i}`;
              newBatches.push({
                pariksha_type: batchSet.parikshaType,
                batch_number: i,
                batch_name: batchName,
                parikshaadhikari_ids: batchExaminers[batchKey] || [],
                is_special_ghanam:
                  batchSet.parikshaType === 'krishna_yajurveda' ? (batchSpecialGhanam[batchKey] || false) : false,
              });
            }
          }
        });

        // Find batches that need to be deleted (exist in DB but not in form)
        const batchesToDelete = parikshaBatches.filter(batch => !batchesInForm.has(batch._id));

        // Delete removed batches and their assignments
        if (batchesToDelete.length > 0) {
          for (const batch of batchesToDelete) {
            await api.delete(`/api/pariksha-batch/${batch._id}`);
            
            // Also remove vatu assignments for this batch
            if (savedVatuAssignments[batch._id]) {
              const updatedAssignments = { ...savedVatuAssignments };
              delete updatedAssignments[batch._id];
              setSavedVatuAssignments(updatedAssignments);
              
              // Update in database
              await api.post('/api/vatu-assignments', {
                year: teluguYearTelugu,
                assignments: updatedAssignments,
                assignment_mode: assignmentMode
              });
            }
          }
        }

        // Update changed batches
        if (updates.length > 0) {
          for (const update of updates) {
            await api.put(`/api/pariksha-batch/${update.batchId}`, {
              parikshaadhikari_ids: update.parikshaadhikari_ids,
              is_special_ghanam: update.is_special_ghanam,
            });
          }
        }

        // Create new batches
        if (newBatches.length > 0) {
          await api.post('/api/pariksha-batch/bulk-create', {
            year: teluguYearTelugu,
            batches: newBatches,
          });
        }

        setIsEditingBatches(false);
        fetchData();
        handleCloseBatchDialog();
      } else {
        // CREATE MODE: Create new batches
        const batches = [];

        batchForm.parikshaTypes.forEach((batchSet, setIndex) => {
          const parikshaType = parikshaTypes.find((pt) => pt.value === batchSet.parikshaType);
          const numberOfBatches = batchSet.numberOfBatches;

          for (let i = 1; i <= numberOfBatches; i++) {
            const batchKey = `${batchSet.parikshaType}_${setIndex}_${i}`;
            const batchName = `${lang === 'telugu' ? parikshaType.label.telugu : parikshaType.label.sanskrit} - బ్యాచ్ ${i}`;
            batches.push({
              pariksha_type: batchSet.parikshaType,
              batch_number: i,
              batch_name: batchName,
              parikshaadhikari_ids: batchExaminers[batchKey] || [],
              is_special_ghanam:
                batchSet.parikshaType === 'krishna_yajurveda' ? (batchSpecialGhanam[batchKey] || false) : false,
            });
          }
        });

        const res = await api.post('/api/pariksha-batch/bulk-create', {
          year: teluguYearTelugu,
          batches,
        });

        const data = await res.json();
        if (res.ok && data.success) {
          fetchData();
          handleCloseBatchDialog();
        }
      }
    } catch (err) {
      console.error('Error saving batches:', err);
      setError('Failed to save batches');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!isAuthorized) {
    return (
      <Container maxWidth="lg">
        <Paper elevation={3} sx={{ p: 4, mt: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error">
            {lang === 'telugu' ? 'అనుమతి లేదు' : 'अनुमति नहीं'}
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {lang === 'telugu' 
              ? 'ఈ పేజీని చూడటానికి మీకు అధికారం లేదు' 
              : 'आपको यह पृष्ठ देखने की अनुमति नहीं है'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 600 }}>
          {lang === 'telugu' ? 'పరీక్షాధికారి కేటాయింపులు' : 'परीक्षाधिकारि कार्यनियोजनम्'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Parikshaadhikari Accordion */}
        <Accordion 
          expanded={accordionExpanded.parikshaadhikari}
          onChange={(e, isExpanded) => setAccordionExpanded({ ...accordionExpanded, parikshaadhikari: isExpanded })}
          sx={{ mb: 3 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#e8f5e9' }}>
            <Typography variant="h6" sx={{ flex: 1, color: '#2e7d32' }}>
              {currentLabels.parikshaadhikarulu}
            </Typography>
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenParikshaadhikariDialog();
              }}
              title="Add Parikshaadhikari"
            >
              <AddIcon />
            </IconButton>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      #
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {currentLabels.name}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {currentLabels.parikshaType}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {currentLabels.phone}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parikshaadhikaris.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan="5" align="center" sx={{ py: 3 }}>
                        {lang === 'telugu' ? 'డేటా లేదు' : 'डेटा नहीं है'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    parikshaadhikaris.map((item, index) => (
                      <TableRow 
                        key={item._id} 
                        sx={{ 
                          '&:hover': { backgroundColor: '#f9f9f9' },
                          opacity: item.active === false ? 0.5 : 1,
                          backgroundColor: item.active === false ? '#f5f5f5' : 'inherit'
                        }}
                      >
                        <TableCell align="center">{index + 1}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.telugu}
                              {item.active === false && (
                                <Chip 
                                  label={lang === 'telugu' ? 'నిష్క్రియం' : 'निष्क्रियम्'}
                                  size="small"
                                  sx={{ ml: 1, backgroundColor: '#ff9800', color: 'white' }}
                                />
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.sanskrit}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {item.pariksha_types?.map((type) => (
                              <Chip
                                key={type}
                                label={parikshaTypes.find((pt) => pt.value === type)?.label[lang] || type}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>{item.phone}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenParikshaadhikariDialog(item)}
                            disabled={item.active === false}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {item.active === false ? (
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleToggleActiveParikshaadhikari(item._id, false)}
                              title={lang === 'telugu' ? 'తిరిగి సక్రియం చేయండి' : 'पुनः सक्रियं कुरु'}
                            >
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleToggleActiveParikshaadhikari(item._id, true)}
                              title={lang === 'telugu' ? 'నిష్క్రియం చేయండి' : 'निष्क्रियं कुरु'}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                          {item.active !== false && index > 0 && (
                            <IconButton
                              size="small"
                              onClick={() => handleMoveParikshaadhikari(index, 'up')}
                            >
                              <KeyboardArrowUpIcon fontSize="small" />
                            </IconButton>
                          )}
                          {item.active !== false && index < parikshaadhikaris.filter(p => p.active !== false).length - 1 && (
                            <IconButton
                              size="small"
                              onClick={() => handleMoveParikshaadhikari(index, 'down')}
                            >
                              <KeyboardArrowDownIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Pariksha Batches Accordion */}
        <Accordion
          expanded={accordionExpanded.batches}
          onChange={(e, isExpanded) => setAccordionExpanded({ ...accordionExpanded, batches: isExpanded })}
        >
         <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#e8f5e9' }}>
            <Typography variant="h6" sx={{ flex: 1, color: '#2e7d32' }}>
              {currentLabels.parikshaBatches}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {parikshaBatches.length > 0 && (
                <>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditBatches();
                    }}
                    title="Edit Batches"
                  >
                    <EditIcon sx={{ fontSize: '20px' }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (window.confirm('Delete all batches? This cannot be undone.')) {
                        try {
                          parikshaBatches.forEach((batch) => {
                            api.delete(`/api/pariksha-batch/${batch._id}`);
                          });
                          // Also delete vatu assignments from database
                          await api.delete(`/api/vatu-assignments/${teluguYearTelugu}`);
                          
                          setParikshaBatches([]);
                          // Clear vatu assignments when deleting all batches
                          setVatuAssignments({});
                          setSavedVatuAssignments({});
                        } catch (err) {
                          console.error('Error deleting batches and assignments:', err);
                          setError('Failed to delete batches and assignments');
                        }
                      }
                    }}
                    title="Delete All Batches"
                  >
                    <DeleteIcon sx={{ fontSize: '20px' }} />
                  </IconButton>
                </>
              )}
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenBatchDialog();
                }}
                title="Add Batches"
              >
                <AddIcon />
              </IconButton>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {parikshaBatches.length === 0 ? (
                <Grid item xs={12} sx={{ textAlign: 'center', py: 3 }}>
                  {lang === 'telugu' ? 'డేటా లేదు' : 'डेटा नहीं है'}
                </Grid>
              ) : (
                parikshaBatches.map((batch) => (
                  <Grid item xs={12} sm={6} md={4} key={batch._id}>
                    <Card sx={{ backgroundColor: '#e3f2fd', position: 'relative' }}>
                      {batch.is_special_ghanam && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: '#FF6F00',
                            color: 'white',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            zIndex: 1
                          }}
                        >
                          {lang === 'telugu' ? 'ప్రత్యేక' : 'विशेष'}
                        </Box>
                      )}
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          {batch.batch_name}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {batch.parikshaadhikari_ids?.map((examiner) => examiner.telugu).join(', ') ||
                            'No examiners assigned'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Assignment Mode Selection - OUTSIDE ACCORDION */}
        {parikshaBatches.length > 0 && (
          <Box sx={{ mb: 3, p: 3, border: '2px solid #e8f5e9', borderRadius: 1, backgroundColor: '#e8f5e9', mt: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#2e7d32' }}>
              {lang === 'telugu' ? 'విద్యార్థుల కేటాయింపు విధానం ఎంచుకోండి' : 'छात्रानां निर्योगनियमं वरणयतु'}
            </Typography>
            <RadioGroup
              value={assignmentMode}
              onChange={(e) => {
                const newMode = e.target.value;
                // If switching modes and assignments exist, show confirmation
                if (Object.keys(savedVatuAssignments).length > 0 && assignmentMode !== newMode) {
                  setConfirmDialogMessage(
                    lang === 'telugu' 
                      ? 'విధానం మారితే, అన్ని ఇతిమధ్య కేటాయింపులు తొలగిపోతాయి. కొనసాగించాలా?' 
                      : 'मोड परिवर्तित करने से सभी पूर्व नियोजनाएँ हटा दी जाएंगी। जारी रखें?'
                  );
                  setPendingModeChange(newMode);
                  setOpenConfirmDialog(true);
                } else if (newMode === 'auto') {
                  // Auto mode confirmation
                  setConfirmDialogMessage(
                    lang === 'telugu'
                      ? 'స్వయంచాలక విధానంలో అన్ని విద్యార్థుల కేటాయింపు నేరుగా కేటాయించబడతాయి. కొనసాగించాలా?'
                      : 'स्वयंचालित कार्य से सभी वातु सीधे वितरित होंगे। क्या जारी रखें?'
                  );
                  setPendingModeChange(newMode);
                  setOpenConfirmDialog(true);
                } else {
                  setAssignmentMode(newMode);
                }
              }}
              sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}
            >
              <FormControlLabel
                value="auto"
                control={<Radio checked={assignmentMode === 'auto'} sx={{ color: '#2e7d32', '&.Mui-checked': { color: '#2e7d32' } }} />}
                label={lang === 'telugu' ? 'స్వయంచాలక కేటాయింపు' : 'स्वयंचालित निर्योग'}
              />
              <FormControlLabel
                value="manual"
                control={<Radio checked={assignmentMode === 'manual'} sx={{ color: '#2e7d32', '&.Mui-checked': { color: '#2e7d32' } }} />}
                label={lang === 'telugu' ? 'మానవీయ కేటాయింపు' : 'मानवीय निर्योग'}
              />
            </RadioGroup>
            
            {assignmentMode && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  sx={{ backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#27632a' }, position: 'relative' }}
                  onClick={async () => {
                    try {
                      setIsLoadingVatus(true);
                      if (assignmentMode === 'auto') {
                        const { vatus: fetchedVatus, categoriesMap } = await fetchVatus();
                        // Pass fetched data directly to auto-assign
                        await autoAssignVatus(fetchedVatus, categoriesMap);
                      } else {
                        // For manual mode, fetch vatus and populate dialog with saved assignments
                        await fetchVatus();
                        // Clear first row selections
                        setSelectedBatchForAssign(null);
                        setSelectedVatusForAssign([]);
                        setEditingBatchId(null);
                        
                        if (Object.keys(savedVatuAssignments).length > 0) {
                          // Bulk edit mode - populate all saved assignments for display
                          setVatuAssignments(savedVatuAssignments);
                        }
                        setOpenManualAssignDialog(true);
                      }
                    } catch (err) {
                      console.error('Error:', err);
                      setError('An error occurred. Please try again.');
                    } finally {
                      setIsLoadingVatus(false);
                    }
                  }}
                  disabled={isLoadingVatus}
                >
                  {isLoadingVatus ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: '#fff' }} />
                      <span>{lang === 'telugu' ? 'లోడ్ చేస్తున్నారు...' : 'लोड हो रहा है...'}</span>
                    </Box>
                  ) : (
                    Object.keys(savedVatuAssignments).length > 0
                      ? (lang === 'telugu' ? 'సవరించండి' : 'संपादयतु')
                      : (lang === 'telugu' ? 'ప్రారంభించండి' : 'प्रारंभयतु')
                  )}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Saved Vatu Assignments Accordion */}
        {Object.keys(savedVatuAssignments).length > 0 && (
          <Accordion sx={{ mb: 3, mt: 3 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#e8f5e9' }}>
              <Typography variant="h6" sx={{ flex: 1, color: '#2e7d32' }}>
                {lang === 'telugu' ? 'బ్యాచ్ - విద్యార్థులు కేటాయింపులు' : ''}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {Object.entries(savedVatuAssignments).map(([batchId, vatuDataArray]) => {
                  if (vatuDataArray.length === 0) return null;
                  
                  const batch = parikshaBatches.find(b => b._id === batchId);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={batchId}>
                      <Card sx={{ border: '2px solid #2e7d32', backgroundColor: '#f1f8e9' }}>
                        <CardContent>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#2e7d32' }}>
                            {batch?.batch_name}
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {vatuDataArray.map((vatuData) => {
                              // Handle both old format (string) and new format (object)
                              const vatuId = typeof vatuData === 'string' ? vatuData : vatuData.vatu_id;
                              const vatuName = typeof vatuData === 'string' ? null : vatuData.vatu_name;
                              
                              const vatu = vatus.find(v => v.vatu_id === vatuId);
                              const displayName = vatuName || vatu?.vatu_name || vatuId;
                              const isKrishnaYajurveda = batch?.pariksha_type === 'krishna_yajurveda';
                              let displayLabel;
                              if (isKrishnaYajurveda) {
                                // For Krishna Yajurveda, calculate category dynamically
                                displayLabel = categorizeVatu(vatu, lang);
                              } else {
                                // For non-Krishna, use pariksha_type
                                displayLabel = vatu?.pariksha_type;
                              }
                              return (
                                <Box key={vatuId} sx={{ p: 1, backgroundColor: '#fff', borderRadius: 1, borderLeft: '3px solid #2e7d32' }}>
                                  <Typography variant="body2">
                                    {`${displayName} - ${displayLabel}`}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}
      </Paper>

      {/* Manual Assignment Dialog */}
      {assignmentMode === 'manual' && openManualAssignDialog && (
        <Dialog open={openManualAssignDialog} onClose={() => { setOpenManualAssignDialog(false); setEditingBatchId(null); }} maxWidth="md" fullWidth>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <DialogTitle sx={{ p: 0 }}>
              {lang === 'telugu' ? 'వటువులను బ్యాచ్‌కు కేటాయించండి' : 'वातुनां समूहे निर्योजयतु'}
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={() => { setOpenManualAssignDialog(false); setEditingBatchId(null); }}
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <DialogContent sx={{ pt: 3 }}>
            {/* First Row: Batch Selection and Vatu Multi-Select */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              {/* Batch Dropdown */}
              <FormControl sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <InputLabel>{lang === 'telugu' ? 'బ్యాచ్ ఎంచుకోండి' : 'समूह वरणयतु'}</InputLabel>
                <Select
                  value={selectedBatchForAssign?._id || ''}
                  onChange={(e) => {
                    if (e.target.value === '') {
                      setSelectedBatchForAssign(null);
                    } else {
                      const batch = parikshaBatches.find(b => b._id === e.target.value);
                      setSelectedBatchForAssign(batch);
                    }
                    setSelectedVatusForAssign([]);
                    setHasPendingFirstRowChanges(true);
                  }}
                  label={lang === 'telugu' ? 'బ్యాచ్ ఎంచుకోండి' : 'समूह वरणयतु'}
                  onClear={() => {
                    setSelectedBatchForAssign(null);
                    setSelectedVatusForAssign([]);
                    setHasPendingFirstRowChanges(true);
                  }}
                >
                  <MenuItem value="">
                    <em>{lang === 'telugu' ? 'ఎంపిక చేయకండి' : 'कोई नहीं'}</em>
                  </MenuItem>
                  {parikshaBatches
                    .filter(batch => {
                      return !vatuAssignments[batch._id] || vatuAssignments[batch._id].length === 0 || batch._id === editingBatchId;
                    })
                    .map((batch) => (
                      <MenuItem key={batch._id} value={batch._id}>
                        {batch.batch_name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {/* Vatu Multi-Select */}
              <FormControl sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <InputLabel>{lang === 'telugu' ? 'వటువులను ఎంచుకోండి' : 'वातुनां वरणयतु'}</InputLabel>
                <Select
                  multiple
                  value={selectedVatusForAssign}
                  onChange={(e) => {
                    setSelectedVatusForAssign(e.target.value);
                    setHasPendingFirstRowChanges(true);
                  }}
                  label={lang === 'telugu' ? 'వటువులను ఎంచుకోండి' : 'वातुनां वरणयतु'}
                  disabled={!selectedBatchForAssign}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((vatuId) => {
                        const vatu = vatus.find(v => v.vatu_id === vatuId);
                        const isKrishnaYajurveda = selectedBatchForAssign?.pariksha_type === 'krishna_yajurveda';
                        let displayLabel;
                        if (isKrishnaYajurveda) {
                          // For Krishna Yajurveda, calculate category dynamically
                          displayLabel = categorizeVatu(vatu, lang);
                        } else {
                          // For non-Krishna, use pariksha_type
                          displayLabel = vatu?.pariksha_type;
                        }
                        return (
                          <Chip
                            key={vatuId}
                            label={`${vatu?.vatu_name} - ${displayLabel}`}
                            size="small"
                            onDelete={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedVatusForAssign(selectedVatusForAssign.filter(id => id !== vatuId));
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            sx={{ maxWidth: '100%' }}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {(() => {
                    // Don't show any vatus if no batch is selected
                    if (!selectedBatchForAssign) {
                      return (
                        <MenuItem disabled>
                          {lang === 'telugu' ? 'ముందుగా బ్యాచ్ ఎంచుకోండి' : 'पहले समूह चुनें'}
                        </MenuItem>
                      );
                    }
                    
                    // Calculate available vatus: unassigned for the current pariksha type
                    const vatuAssignedForThisParikshaType = new Set();
                    
                    // Get the current batch's pariksha type
                    const currentBatchParikshaType = selectedBatchForAssign?.pariksha_type;
                    
                    // Get vatus that are assigned to batches with the SAME pariksha type, BUT exclude the batch being edited
                    Object.entries(vatuAssignments).forEach(([batchId, vatuIds]) => {
                      // Skip the batch being edited - its vatus should be available for re-selection
                      if (batchId === editingBatchId) {
                        return;
                      }
                      
                      // Only exclude vatus if the batch has the same pariksha type
                      const batch = parikshaBatches.find(b => b._id === batchId);
                      if (batch && batch.pariksha_type === currentBatchParikshaType) {
                        vatuIds.forEach(vatuId => {
                          const vatuIdToAdd = typeof vatuId === 'string' ? vatuId : vatuId.vatu_id;
                          vatuAssignedForThisParikshaType.add(vatuIdToAdd);
                        });
                      }
                    });
                    
                    // Filter available vatus: unassigned for this pariksha type + matching pariksha type
                    const availableVatus = vatus
                      .filter(vatu => {
                        // Filter by pariksha_type - only show vatus that match the selected batch's pariksha_type
                        if (currentBatchParikshaType) {
                          const vatuParikshaTypes = Array.isArray(vatu.pariksha_type) ? vatu.pariksha_type : [vatu.pariksha_type];
                          if (!vatuParikshaTypes.includes(currentBatchParikshaType)) {
                            return false;
                          }
                        }
                        return true;
                      })
                      .filter(vatu => !vatuAssignedForThisParikshaType.has(vatu.vatu_id))
                      .filter(vatu => !selectedVatusForAssign.includes(vatu.vatu_id));
                    
                    // Group vatus by pathasala_name
                    const groupedByPathasala = {};
                    availableVatus.forEach(vatu => {
                      const pathasalaName = vatu.pathasala_name || 'Other';
                      if (!groupedByPathasala[pathasalaName]) {
                        groupedByPathasala[pathasalaName] = [];
                      }
                      groupedByPathasala[pathasalaName].push(vatu);
                    });
                    
                    // Render grouped vatus
                    return Object.entries(groupedByPathasala).map(([pathasalaName, pathasalaVatus]) => [
                      // Render pathasala name as disabled header
                      <MenuItem
                        key={`pathasala-${pathasalaName}`}
                        disabled
                        sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', color: '#333' }}
                      >
                        <strong>{pathasalaName}</strong>
                      </MenuItem>,
                      // Render vatus under this pathasala
                      ...pathasalaVatus.map((vatu) => {
                        const isKrishnaYajurveda = selectedBatchForAssign?.pariksha_type === 'krishna_yajurveda';
                        let displayLabel;
                        if (isKrishnaYajurveda) {
                          // For Krishna Yajurveda, calculate category dynamically
                          displayLabel = categorizeVatu(vatu, lang);
                        } else {
                          // For non-Krishna, use pariksha_type
                          displayLabel = vatu.pariksha_type;
                        }
                        return (
                          <MenuItem 
                            key={vatu.vatu_id} 
                            value={vatu.vatu_id}
                            sx={{ pl: 4 }}
                          >
                            {`${vatu.vatu_name} - ${displayLabel}`}
                          </MenuItem>
                        );
                      })
                    ]).flat();
                  })()}
                </Select>
              </FormControl>

              {/* Assign Button */}
              <Box sx={{ display: 'flex', alignItems: 'flex-end', flex: '0 1 auto' }}>
                <Button
                  variant="contained"
                  onClick={handleManualAssignment}
                  disabled={!selectedBatchForAssign || selectedVatusForAssign.length === 0 || isAssigningVatus}
                  sx={{ backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#27632a' }, height: '56px', px: 3 }}
                >
                  {isAssigningVatus ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: '#fff' }} />
                    </Box>
                  ) : (
                    lang === 'telugu' ? 'కేటాయించండి' : 'निर्योजयतु'
                  )}
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Second Row: Display Assigned Vatus by Batch */}
            {Object.keys(vatuAssignments).length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#7f4b2d' }}>
                  {lang === 'telugu' ? 'కేటాయించిన వటువులు' : 'निर्योजित वातुः'}
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(vatuAssignments).map(([batchId, vatuDataArray]) => {
                    if (vatuDataArray.length === 0) return null;
                    
                    const batch = parikshaBatches.find(b => b._id === batchId);
                    return (
                      <Grid item xs={12} sm={6} md={4} key={batchId}>
                        <Card sx={{ border: '1px solid #7f4b2d', backgroundColor: '#f0f4f8', position: 'relative' }}>
                          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                setEditingBatchId(batchId);
                                setSelectedBatchForAssign(batch);
                                // Extract vatu_ids for the dialog
                                const vatuIds = vatuDataArray.map(vatuData => 
                                  typeof vatuData === 'string' ? vatuData : vatuData.vatu_id
                                );
                                setSelectedVatusForAssign(vatuIds);
                                setHasPendingFirstRowChanges(false); // Reset when editing existing batch
                                // Don't change vatuAssignments - keep all cards displayed
                                setOpenManualAssignDialog(true);
                              }}
                              title={lang === 'telugu' ? 'సవరించండి' : 'संपादयतु'}
                            >
                              <CreateIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <CardContent sx={{ pt: 3 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#7f4b2d' }}>
                              {batch?.batch_name}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {vatuDataArray.map((vatuData) => {
                                // Handle both old format (string) and new format (object)
                                const vatuId = typeof vatuData === 'string' ? vatuData : vatuData.vatu_id;
                                const vatuName = typeof vatuData === 'string' ? null : vatuData.vatu_name;
                                
                                const vatu = vatus.find(v => v.vatu_id === vatuId);
                                const displayName = vatuName || vatu?.vatu_name || vatuId;
                                const isKrishnaYajurveda = batch?.pariksha_type === 'krishna_yajurveda';
                                let displayLabel;
                                if (isKrishnaYajurveda) {
                                  // For Krishna Yajurveda, calculate category dynamically
                                  displayLabel = categorizeVatu(vatu, lang);
                                } else {
                                  // For non-Krishna, use pariksha_type
                                  displayLabel = vatu?.pariksha_type;
                                }
                                return (
                                  <Box key={vatuId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, backgroundColor: '#fff', borderRadius: 1 }}>
                                    <Typography variant="body2">
                                      {`${displayName} - ${displayLabel}`}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => {
                                        const newAssignments = { ...vatuAssignments };
                                        newAssignments[batchId] = newAssignments[batchId].filter(item => {
                                          const itemVatuId = typeof item === 'string' ? item : item.vatu_id;
                                          return itemVatuId !== vatuId;
                                        });
                                        if (newAssignments[batchId].length === 0) {
                                          delete newAssignments[batchId];
                                        }
                                        setVatuAssignments(newAssignments);
                                        setPendingVatus([...pendingVatus, vatuId]);
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                );
                              })}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
            <Button onClick={() => { 
              setOpenManualAssignDialog(false); 
              setEditingBatchId(null); 
              setSelectedBatchForAssign(null);
              setSelectedVatusForAssign([]);
              setHasPendingFirstRowChanges(false);
            }}>
              {lang === 'telugu' ? 'రద్దు చేయండి' : 'रद्दु कुरु'}
            </Button>
            <Button 
              onClick={async () => {
                // Check if there are pending first-row changes
                if (hasPendingFirstRowChanges && (selectedBatchForAssign || selectedVatusForAssign.length > 0)) {
                  alert(
                    lang === 'telugu' 
                      ? 'దయచేసి మీరు చేసిన మార్పులను కేటాయించడానికి "కేటాయించండి" బటన్‌ను క్లిక్ చేయండి, లేదా వాటిని తీసివేయండి.'
                      : 'कृपया परिवर्तनों को सहेजने से पहले "निर्योजयतु" बटन क्लिक करें या उन्हें हटा दें।'
                  );
                  return;
                }
                
                // If editing, apply first row changes to vatuAssignments before confirming save
                let finalAssignments = { ...vatuAssignments };
                
                if (editingBatchId && selectedBatchForAssign) {
                  // Update the edited batch with current first row selections
                  if (selectedVatusForAssign.length > 0) {
                    finalAssignments[editingBatchId] = selectedVatusForAssign;
                  } else {
                    // If no vatus selected, remove the batch from assignments
                    delete finalAssignments[editingBatchId];
                  }
                }
                
                // Save without confirmation dialog
                await handleSaveAssignmentsToDb(finalAssignments);
                
                // Close dialog after successful save
                setOpenManualAssignDialog(false);
                setEditingBatchId(null);
                setSelectedBatchForAssign(null);
                setSelectedVatusForAssign([]);
                setHasPendingFirstRowChanges(false);
              }}
              variant="contained"
              sx={{ backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#27632a' } }}
              disabled={Object.keys(vatuAssignments).length === 0 || isSavingAssignments}
            >
              {isSavingAssignments ? (
                <CircularProgress size={24} sx={{ mr: 1 }} />
              ) : null}
              {lang === 'telugu' ? 'సేవ్ చేయండి' : 'सँरक्षयतु'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {lang === 'telugu' ? 'నిర్ధారణ అవసరం' : 'पुष्टिकरण आवश्यक है'}
        </DialogTitle>
        <DialogContent>
          <Typography>{confirmDialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenConfirmDialog(false); setPendingModeChange(null); }}>
            {lang === 'telugu' ? 'రద్దు చేయండి' : 'रद्दु कुरु'}
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#27632a' } }}
            onClick={() => {
              if (pendingModeChange) {
                setAssignmentMode(pendingModeChange);
                // Clear saved assignments if mode changed
                if (Object.keys(savedVatuAssignments).length > 0) {
                  setSavedVatuAssignments({});
                  setVatuAssignments({});
                  setPendingVatus([]);
                }
              }
              setOpenConfirmDialog(false);
              setPendingModeChange(null);
            }}
          >
            {lang === 'telugu' ? 'కొనసాగించండి' : 'जारी रखें'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Parikshaadhikari Dialog */}
      <Dialog open={openParikshaadhikariDialog} onClose={handleCloseParikshaadhikariDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {teluguYearTelugu} - {currentLabels.addParikshaadhikari}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {/* Photo Upload */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, alignSelf: 'flex-start', color: '#7f4b2d' }}>
              {lang === 'telugu' ? 'పరీక్షాధికారి ఫోటో' : 'परीक्षाधिकारि फोटो'}
            </Typography>
            
            {/* Photo with Config Options */}
            <Box sx={{ position: 'relative', display: 'inline-block', width: 'fit-content' }}>
              <PhotoUpload
                label={lang === 'telugu' ? 'ఫోటో' : 'फोटो'}
                value={parikshaadhikariPhoto}
                onChange={setParikshaadhikariPhoto}
                size={120}
                objectConfig={parikshaadhikariPhotoConfig}
              />
              
              {/* Collapsible Photo Adjustment Section */}
              {parikshaadhikariPhoto && (
                <Accordion sx={{ mt: 2, border: '1px solid #ddd', borderRadius: '8px' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#f8f9fa', '&:hover': { backgroundColor: '#f0f1f2' } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#7f4b2d' }}>
                      {lang === 'telugu' ? 'ఫోటో సర్దుబాటు చేయండి' : 'फोटो समायोजित करें'}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2, backgroundColor: '#fff8ed' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Position Grid */}
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#7f4b2d', mb: 1 }}>
                          {lang === 'telugu' ? 'స్థానం' : 'स्थिति'}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
                          {['top left', 'top', 'top right', 'left', 'center', 'right', 'bottom left', 'bottom', 'bottom right'].map((pos) => (
                            <Box
                              key={pos}
                              onClick={() => setParikshaadhikariPhotoConfig({ ...parikshaadhikariPhotoConfig, position: pos })}
                              sx={{
                                width: '40px',
                                height: '40px',
                                border: '2px solid',
                                borderColor: parikshaadhikariPhotoConfig.position === pos ? '#2e7d32' : '#ddd',
                                backgroundColor: parikshaadhikariPhotoConfig.position === pos ? '#e8f5e9' : '#fff',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '16px',
                                color: parikshaadhikariPhotoConfig.position === pos ? '#2e7d32' : '#999',
                                fontWeight: 600,
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: '#2e7d32',
                                  backgroundColor: '#f0f8f5',
                                  transform: 'scale(1.05)'
                                }
                              }}
                              title={pos}
                            >
                              {pos === 'center' && '●'}
                              {pos === 'top left' && '↖'}
                              {pos === 'top' && '↑'}
                              {pos === 'top right' && '↗'}
                              {pos === 'left' && '←'}
                              {pos === 'right' && '→'}
                              {pos === 'bottom left' && '↙'}
                              {pos === 'bottom' && '↓'}
                              {pos === 'bottom right' && '↘'}
                            </Box>
                          ))}
                        </Box>
                      </Box>

                      {/* Fit Options - 2x2 Grid */}
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#7f4b2d', mb: 1 }}>
                          {lang === 'telugu' ? 'ఫిట్ విధానం' : 'फिट विधि'}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                          {[
                            { value: 'cover', label: lang === 'telugu' ? 'కవర్' : 'कवर', desc: lang === 'telugu' ? 'కత్తిరించు' : 'काटना', icon: '📐' },
                            { value: 'contain', label: lang === 'telugu' ? 'కంటైన్' : 'कंटेन', desc: lang === 'telugu' ? 'పూర్ణ చిత్రం' : 'पूरी छवि', icon: '📦' },
                            { value: 'fill', label: lang === 'telugu' ? 'ఫిల్' : 'भरें', desc: lang === 'telugu' ? 'విస్తరించు' : 'फैलाना', icon: '📏' },
                            { value: 'scale-down', label: lang === 'telugu' ? 'స్కేల్' : 'स्केल', desc: lang === 'telugu' ? 'చిన్న విధానం' : 'छोटा तरीका', icon: '🔽' }
                          ].map((fitOption) => (
                            <Box
                              key={fitOption.value}
                              onClick={() => setParikshaadhikariPhotoConfig({ ...parikshaadhikariPhotoConfig, fit: fitOption.value })}
                              sx={{
                                p: 1.2,
                                border: '2px solid',
                                borderColor: parikshaadhikariPhotoConfig.fit === fitOption.value ? '#2e7d32' : '#ddd',
                                backgroundColor: parikshaadhikariPhotoConfig.fit === fitOption.value ? '#e8f5e9' : '#fff',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: '#2e7d32',
                                  backgroundColor: '#f0f8f5',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 2px 8px rgba(46, 125, 50, 0.15)'
                                }
                              }}
                            >
                              <Typography sx={{ fontSize: '20px', mb: 0.5, textAlign: 'center' }}>
                                {fitOption.icon}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  display: 'block', 
                                  fontWeight: 600, 
                                  color: parikshaadhikariPhotoConfig.fit === fitOption.value ? '#2e7d32' : '#333',
                                  textAlign: 'center'
                                }}
                              >
                                {fitOption.label}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  display: 'block', 
                                  color: '#999',
                                  fontSize: '10px',
                                  textAlign: 'center',
                                  mt: 0.3
                                }}
                              >
                                {fitOption.desc}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          </Box>
          <TextField
            label={currentLabels.name}
            value={parikshaadhikariForm.name}
            onChange={(e) =>
              setParikshaadhikariForm({ ...parikshaadhikariForm, name: e.target.value })
            }
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>{currentLabels.parikshaType}</InputLabel>
            <Select
              multiple
              value={parikshaadhikariForm.selectedParikshaTypes}
              onChange={(e) =>
                setParikshaadhikariForm({
                  ...parikshaadhikariForm,
                  selectedParikshaTypes: e.target.value,
                })
              }
              label={currentLabels.parikshaType}
            >
              {parikshaTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label[lang]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={currentLabels.phone}
            value={parikshaadhikariForm.phone}
            onChange={(e) =>
              setParikshaadhikariForm({ ...parikshaadhikariForm, phone: e.target.value })
            }
            fullWidth
          />
          
          {/* Unavailable Dates Section */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#7f4b2d' }}>
              {lang === 'telugu' ? 'అందుబాటులో లేని తేదీలు' : 'उपलब्ध न दिन'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 2 }}>
              {lang === 'telugu' 
                ? 'మీరు అందుబాటులో లేని తేదీలను ఎంచుకోండి. ఎంపిక చేసిన తేదీలు మీ అందుబాటులో ఉన్న తేదీల నుండి తీసివేయబడతాయి.'
                : 'जिन दिनों आप उपलब्ध नहीं हैं उन्हें चुनें। चयनित दिनों को आपके उपलब्ध दिनों से हटा दिया जाएगा।'}
            </Typography>
            <Box sx={{ 
              border: '1px solid #ddd', 
              borderRadius: 1, 
              p: 2, 
              maxHeight: 300, 
              overflow: 'auto',
              backgroundColor: '#f9f9f9'
            }}>
              {parikshaDates && parikshaDates.length > 0 ? (
                <Stack spacing={1}>
                  {parikshaDates.map((date, index) => {
                    const dateStr = new Date(date).toLocaleDateString('en-CA');
                    const isSelected = parikshaadhikariForm.unavailableDates.some(d => {
                      const d_str = new Date(d).toLocaleDateString('en-CA');
                      return d_str === dateStr;
                    });
                    const displayDate = new Date(date).toLocaleDateString(lang === 'telugu' ? 'te-IN' : 'sa-IN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    });
                    
                    return (
                      <FormControlLabel
                        key={index}
                        control={
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => {
                              const unavailDates = [...parikshaadhikariForm.unavailableDates];
                              if (e.target.checked) {
                                unavailDates.push(date);
                              } else {
                                const idx = unavailDates.findIndex(d => {
                                  const d_str = new Date(d).toLocaleDateString('en-CA');
                                  return d_str === dateStr;
                                });
                                if (idx !== -1) unavailDates.splice(idx, 1);
                              }
                              setParikshaadhikariForm({
                                ...parikshaadhikariForm,
                                unavailableDates: unavailDates
                              });
                            }}
                          />
                        }
                        label={displayDate}
                      />
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ color: '#999' }}>
                  {lang === 'telugu' ? 'తేదీలు లేవు' : 'कोई दिन नहीं'}
                </Typography>
              )}
            </Box>
          </Box>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={parikshaadhikariForm.trustMember}
                onChange={(e) =>
                  setParikshaadhikariForm({
                    ...parikshaadhikariForm,
                    trustMember: e.target.checked,
                  })
                }
              />
            }
            label={currentLabels.trustMember}
          />
          
          {/* Attendance Checkbox - Only show when editing */}
          {editingParikshaadhikari && (
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, backgroundColor: '#f9fafb' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#7f4b2d' }}>
                {lang === 'telugu' ? 'హాజరు' : 'उपस्थितिः'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
                {lang === 'telugu' 
                  ? `నేటి తేదీ (${new Date().toLocaleDateString('te-IN', { year: 'numeric', month: 'long', day: 'numeric' })}) కోసం హాజరు గుర్తు చేయండి`
                  : `आज की तिथि (${new Date().toLocaleDateString('sa-IN', { year: 'numeric', month: 'long', day: 'numeric' })}) के लिए उपस्थिति अंकित करें`}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={parikshaadhikariForm.attendance}
                    onChange={(e) =>
                      setParikshaadhikariForm({
                        ...parikshaadhikariForm,
                        attendance: e.target.checked,
                      })
                    }
                    sx={{ color: '#2e7d32', '&.Mui-checked': { color: '#2e7d32' } }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {lang === 'telugu' ? 'హాజరు అయ్యారు' : 'उपस्थितः'}
                  </Typography>
                }
              />
            </Box>
          )}
          
          {/* Signature Upload */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#7f4b2d' }}>
              {lang === 'telugu' ? 'పరీక్షాధికారి సంతకం' : 'परीक्षाधिकारि हस्ताक्षरम्'}
            </Typography>
            <SignatureCanvas
              label={lang === 'telugu' ? 'సంతకం' : 'हस्ताक्षरम्'}
              value={parikshaadhikariSignature}
              onChange={setParikshaadhikariSignature}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseParikshaadhikariDialog}>{currentLabels.cancel}</Button>
          <Button onClick={handleSaveParikshaadhikari} variant="contained" color="primary">
            {editingParikshaadhikari ? currentLabels.update : currentLabels.create}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Dialog */}
      <Dialog open={openBatchDialog} onClose={handleCloseBatchDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {teluguYearTelugu} - {currentLabels.addBatches}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {/* Add new pariksha type section */}
          <Box sx={{ border: '1px dashed #ccc', borderRadius: 1, p: 2, backgroundColor: '#fafafa' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#7f4b2d' }}>
              {lang === 'telugu' ? 'క్రొత్త పరీక్ష రకం జోడించండి' : 'नव परीक्षा प्रकारम्'}
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{currentLabels.parikshaType}</InputLabel>
              <Select
                value={currentBatchSetup.parikshaType}
                onChange={(e) => setCurrentBatchSetup({ ...currentBatchSetup, parikshaType: e.target.value })}
                label={currentLabels.parikshaType}
              >
                {parikshaTypes
                  .filter((type) => !batchForm.parikshaTypes.some((b) => b.parikshaType === type.value))
                  .map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label[lang]}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <TextField
              label={currentLabels.numberOfBatches}
              type="number"
              value={currentBatchSetup.numberOfBatches}
              onChange={(e) => setCurrentBatchSetup({ ...currentBatchSetup, numberOfBatches: e.target.value })}
              fullWidth
              sx={{ mb: 2 }}
              inputProps={{ min: 1 }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddParikshaBatchSet}
              fullWidth
              disabled={!currentBatchSetup.parikshaType || !currentBatchSetup.numberOfBatches}
            >
              {lang === 'telugu' ? '+ జోడించండి' : '+ जोडयतु'}
            </Button>
          </Box>

          {/* Display added pariksha types */}
          {batchForm.parikshaTypes.map((batchSet, setIndex) => {
            const parikshaTypeObj = parikshaTypes.find((pt) => pt.value === batchSet.parikshaType);
            return (
              <Box key={setIndex} sx={{ border: '2px solid #7f4b2d', borderRadius: 1, p: 3, backgroundColor: '#f0f4f8' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#7f4b2d' }}>
                    {parikshaTypeObj?.label[lang]}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleRemoveParikshaBatchSet(setIndex)}
                  >
                    Remove
                  </Button>
                </Box>

                {/* Batch cards */}
                {Array.from({ length: batchSet.numberOfBatches }).map((_, i) => {
                  const batchKey = `${batchSet.parikshaType}_${setIndex}_${i + 1}`;
                  return (
                    <Box
                      key={i}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        p: 2,
                        backgroundColor: '#fff',
                        mb: i < batchSet.numberOfBatches - 1 ? 2 : 0,
                        position: 'relative',
                      }}
                    >
                      {/* Batch header with delete button */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#7f4b2d' }}>
                          {`బ్యాచ్ ${i + 1}`}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            const batchNumberToDelete = i + 1; // 1-based batch number being deleted
                            const totalBatches = batchSet.numberOfBatches;
                            const pType = batchSet.parikshaType;
                            const sIdx = setIndex;
                            
                            // Update selected examiners set - remove deleted batch's examiners
                            const deletedBatchKey = `${pType}_${sIdx}_${batchNumberToDelete}`;
                            const oldExaminers = batchExaminers[deletedBatchKey] || [];
                            const newSelectedSet = new Set(allSelectedExaminers);
                            oldExaminers.forEach((id) => newSelectedSet.delete(id));
                            
                            // Build new batch examiners and special ghanam with re-indexed keys
                            const newBatchExaminers = {};
                            const newBatchSpecialGhanam = {};
                            
                            // Copy all keys that don't belong to this pariksha type set
                            Object.keys(batchExaminers).forEach((key) => {
                              if (!key.startsWith(`${pType}_${sIdx}_`)) {
                                newBatchExaminers[key] = batchExaminers[key];
                              }
                            });
                            Object.keys(batchSpecialGhanam).forEach((key) => {
                              if (!key.startsWith(`${pType}_${sIdx}_`)) {
                                newBatchSpecialGhanam[key] = batchSpecialGhanam[key];
                              }
                            });
                            
                            // Re-index the batches for this pariksha type set
                            // Skip the deleted batch and shift subsequent batches down
                            let newBatchNum = 1;
                            for (let oldBatchNum = 1; oldBatchNum <= totalBatches; oldBatchNum++) {
                              if (oldBatchNum === batchNumberToDelete) {
                                // Skip the deleted batch
                                continue;
                              }
                              const oldKey = `${pType}_${sIdx}_${oldBatchNum}`;
                              const newKey = `${pType}_${sIdx}_${newBatchNum}`;
                              
                              if (batchExaminers[oldKey] !== undefined) {
                                newBatchExaminers[newKey] = batchExaminers[oldKey];
                              }
                              if (batchSpecialGhanam[oldKey] !== undefined) {
                                newBatchSpecialGhanam[newKey] = batchSpecialGhanam[oldKey];
                              }
                              newBatchNum++;
                            }
                            
                            // Decrement numberOfBatches
                            const newBatchSet = {
                              parikshaType: pType,
                              numberOfBatches: Math.max(1, totalBatches - 1),
                            };
                            const updatedBatches = batchForm.parikshaTypes.map((batch, idx) =>
                              idx === sIdx ? newBatchSet : batch
                            );
                            
                            setBatchForm({ parikshaTypes: updatedBatches });
                            setBatchExaminers(newBatchExaminers);
                            setBatchSpecialGhanam(newBatchSpecialGhanam);
                            setAllSelectedExaminers(newSelectedSet);
                          }}
                          title="Delete this batch"
                        >
                          <DeleteIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                      </Box>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>{`బ్యాచ్ ${i + 1} - ${currentLabels.selectExaminers}`}</InputLabel>
                        <Select
                          multiple
                          value={batchExaminers[batchKey] || []}
                          onChange={(e) => {
                            const newSelected = e.target.value;
                            const oldSelected = batchExaminers[batchKey] || [];
                            const newSelectedSet = new Set(allSelectedExaminers);

                            oldSelected.forEach((id) => newSelectedSet.delete(id));
                            newSelected.forEach((id) => newSelectedSet.add(id));

                            setBatchExaminers({ ...batchExaminers, [batchKey]: newSelected });
                            setAllSelectedExaminers(newSelectedSet);
                          }}
                          label={`బ్యాచ్ ${i + 1} - ${currentLabels.selectExaminers}`}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => {
                                const examiner = getExaminerById(value);
                                const isAssociation = examiner?.trust_member === true;
                                return (
                                  <Chip
                                    key={value}
                                    label={examiner?.telugu || examiner?.sanskrit || 'Unknown'}
                                    size="small"
                                    onDelete={(e) => {
                                      // Prevent any event propagation
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
                                      
                                      // Remove from selection
                                      const newSelected = (batchExaminers[batchKey] || []).filter((id) => id !== value);
                                      const newSelectedSet = new Set(allSelectedExaminers);
                                      newSelectedSet.delete(value);
                                      setBatchExaminers({ ...batchExaminers, [batchKey]: newSelected });
                                      setAllSelectedExaminers(newSelectedSet);
                                    }}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    sx={{
                                      backgroundColor: isAssociation ? '#FFF3E0' : '#E3F2FD',
                                      color: isAssociation ? '#E65100' : '#7f4b2d',
                                      fontWeight: '600',
                                      pointerEvents: 'auto',
                                      '& .MuiChip-deleteIcon': {
                                        pointerEvents: 'auto',
                                      }
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          )}
                        >
                          {parikshaadhikaris
                            .filter(
                              (examiner) => {
                                // Check if examiner is qualified for this pariksha type
                                const isQualified = !examiner.pariksha_types ||
                                  examiner.pariksha_types.includes(batchSet.parikshaType);
                                
                                // Check if examiner is already selected in CURRENT batch
                                const currentBatchExaminers = batchExaminers[batchKey] || [];
                                const isInCurrentBatch = currentBatchExaminers.includes(examiner._id);
                                
                                // Check if examiner is selected in OTHER batches of the SAME pariksha type SET
                                // Use the expected prefix to correctly identify keys belonging to this set
                                const expectedKeyPrefix = `${batchSet.parikshaType}_${setIndex}_`;
                                const isInOtherBatchOfSameType = Object.keys(batchExaminers).some(key => {
                                  return key.startsWith(expectedKeyPrefix) && 
                                         key !== batchKey && 
                                         batchExaminers[key].includes(examiner._id);
                                });
                                
                                // Show if qualified AND not already selected in this batch AND not in other batches of same type
                                return isQualified && !isInCurrentBatch && !isInOtherBatchOfSameType;
                              }
                            )
                            .map((examiner) => (
                              <MenuItem
                                key={examiner._id}
                                value={examiner._id}
                                sx={
                                  examiner.trust_member === true
                                    ? {
                                        backgroundColor: '#FFF3E0',
                                        '&:hover': { backgroundColor: '#FFE0B2' },
                                      }
                                    : {}
                                }
                              >
                                {examiner.trust_member === true && (
                                  <Box
                                    sx={{
                                      width: '8px',
                                      height: '8px',
                                      borderRadius: '50%',
                                      backgroundColor: '#FF9800',
                                      flexShrink: 0,
                                      mr: 1,
                                    }}
                                  />
                                )}
                                {examiner.telugu}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>

                      {/* Special Ghanam Checkbox - Only for krishna_yajurveda */}
                      {batchSet.parikshaType === 'krishna_yajurveda' && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={batchSpecialGhanam[batchKey] || false}
                              onChange={(e) => setBatchSpecialGhanam({ ...batchSpecialGhanam, [batchKey]: e.target.checked })}
                            />
                          }
                          label={lang === 'telugu' ? 'విశేష ఘన, క్రమ పరీక్షల పరీక్షాధికారులు' : 'विशेष घनपरीक्षा परीक्षाधिकारिणः'}
                        />
                      )}
                    </Box>
                  );
                })}
                {/* Add more batches button */}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => {
                      const newBatchSet = {
                        parikshaType: batchSet.parikshaType,
                        numberOfBatches: batchSet.numberOfBatches + 1,
                      };
                      const updatedBatches = batchForm.parikshaTypes.map((batch, idx) =>
                        idx === setIndex ? newBatchSet : batch
                      );
                      setBatchForm({ parikshaTypes: updatedBatches });
                    }}
                    title="Add another batch"
                  >
                    <AddIcon sx={{ fontSize: '24px' }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })}

        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBatchDialog}>{currentLabels.cancel}</Button>
          <Button onClick={handleSaveBatches} variant="contained" color="primary">
            {currentLabels.create}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ExaminerAssignments;
